import * as logging from 'util/Logging';

/**
 * Anchor-based scroll preservation for the Note editor.
 *
 * Capture the topmost visible note part (a `data-provenance-id` element inside `.noteEditor-scrollContainer`),
 * along with its offset from the container top. After the entry/document is reloaded — at which point every
 * note part has a brand new client-side cid but its server-side `provenanceId` is unchanged — look the
 * element back up by `provenanceId` and adjust `scrollTop` so that the anchor lands at the same offset from
 * the container top as before.
 *
 * Why an anchor and not the raw `scrollTop`?
 *   After reload, the React tree remounts with new cids, virtualization treats every item as the initial
 *   render again, and items above the user's viewport often render as skeletons with stale-or-default
 *   heights. Restoring the raw `scrollTop` would put the user at a different content location (and the
 *   browser may even silently clamp it if the new `scrollHeight` is temporarily smaller). Anchoring on a
 *   stable server-side id and aligning its offset preserves the user's view regardless of layout shifts
 *   above the anchor.
 */

const SCROLL_CONTAINER_SELECTOR = '.noteEditor-scrollContainer';
const PROVENANCE_ID_ATTR = 'data-provenance-id';
const FILE_ID_ATTR = 'data-file-id';

export interface SavedScrollAnchor {
  /** Id of the entry/template this was captured in; used to find the scroll container on restore. */
  fileId: string;
  /** Stable server-side id of the anchor note part, or null if no anchor was found. */
  provenanceId: string | null;
  /** Distance from the top of the scroll container to the top of the anchor element, in pixels. */
  topOffset: number;
  /** Raw scrollTop captured at save time, used as a fallback when no anchor is available or findable. */
  fallbackScrollTop: number;
}

/**
 * Find the note editor scroll container for a given file id, or null if none is present.
 */
function findScrollContainer(fileId: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(
    `[${FILE_ID_ATTR}="${CSS.escape(fileId)}"] ${SCROLL_CONTAINER_SELECTOR}`
  );
}

/**
 * Within `scrollContainer`, find the topmost element with a `data-provenance-id` whose top is at or below
 * the container's top edge (i.e. the first visible note part). Returns null if no such element exists.
 */
function findTopVisibleAnchor(
  scrollContainer: HTMLElement
): {element: HTMLElement; provenanceId: string; topOffset: number} | null {
  const containerTop = scrollContainer.getBoundingClientRect().top;
  const candidates = scrollContainer.querySelectorAll<HTMLElement>(`[${PROVENANCE_ID_ATTR}]`);
  let best: {element: HTMLElement; provenanceId: string; topOffset: number} | null = null;
  for (const element of Array.from(candidates)) {
    const provenanceId = element.getAttribute(PROVENANCE_ID_ATTR);
    if (!provenanceId) {
      continue;
    }
    const rect = element.getBoundingClientRect();
    const topOffset = rect.top - containerTop;
    // Skip elements that ended above the container top entirely — those are scrolled out of view.
    if (rect.bottom <= containerTop) {
      continue;
    }
    // The first candidate whose top is at or below the container top is the topmost visible anchor.
    // We don't need to scan further since `querySelectorAll` returns elements in document order.
    if (topOffset >= 0) {
      return {element, provenanceId, topOffset};
    }
    // Otherwise the element straddles the top edge; remember it as a candidate but keep looking for a
    // cleaner anchor below.
    if (best == null) {
      best = {element, provenanceId, topOffset};
    }
  }
  return best;
}

/**
 * Capture the current scroll position of the note editor scrolled to the entry/template/document with the
 * given id. Returns `null` if no scroll container can be found (e.g. the user submitted from a non-editor
 * context, or the editor is not yet mounted/has no layout).
 */
export function captureScrollAnchor(fileId: string): SavedScrollAnchor | null {
  const scrollContainer = findScrollContainer(fileId);
  if (scrollContainer == null) {
    return null;
  }
  // A container inside a display: none subtree still matches the selector, but reports a scrollTop of
  // 0 and zero-sized rects for every note part. Don't capture this.
  if (scrollContainer.getBoundingClientRect().height === 0) {
    return null;
  }
  const fallbackScrollTop = scrollContainer.scrollTop;
  const anchor = findTopVisibleAnchor(scrollContainer);
  return {
    fileId,
    provenanceId: anchor?.provenanceId ?? null,
    topOffset: anchor?.topOffset ?? 0,
    fallbackScrollTop,
  };
}

/**
 * Apply the saved anchor to the scroll container, adjusting `scrollTop` so that the anchor element lands
 * back at its original offset from the container top. Falls back to the raw saved scrollTop if the anchor
 * cannot be found (e.g. the note part was deleted, or never had a server-side provenanceId).
 *
 * Caller should invoke this after the post-action rerender has begun. We schedule the actual layout reads
 * inside `requestAnimationFrame`s so that React has flushed its commit and the browser has performed
 * layout. We restore twice on consecutive frames to catch late-arriving rerenders from
 * `doPostRefresh` / `postServerSideUpdate` and virtualization devirtualizing items.
 */
export function restoreScrollAnchor(saved: SavedScrollAnchor): void {
  const apply = (): void => {
    try {
      const {fileId, provenanceId, topOffset, fallbackScrollTop} = saved;
      const scrollContainer = findScrollContainer(fileId);
      if (scrollContainer == null) {
        return;
      }
      if (provenanceId != null) {
        const element = scrollContainer.querySelector<HTMLElement>(
          `[${PROVENANCE_ID_ATTR}="${CSS.escape(provenanceId)}"]`
        );
        if (element != null) {
          const containerTop = scrollContainer.getBoundingClientRect().top;
          const currentTopOffset = element.getBoundingClientRect().top - containerTop;
          const delta = currentTopOffset - topOffset;
          // Avoid mutating scrollTop when nothing has moved; this prevents triggering scroll listeners
          // unnecessarily.
          if (Math.abs(delta) > 0.5) {
            scrollContainer.scrollTop += delta;
          }
          return;
        }
      }
      // Fallback: the anchor element was lost (deleted, or no anchor was captured). Set the raw scrollTop
      // so the user is at least back somewhere reasonable rather than at the top of the document.
      if (scrollContainer.scrollTop !== fallbackScrollTop) {
        scrollContainer.scrollTop = fallbackScrollTop;
      }
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (err) {
      // Scroll restoration is best-effort: it is invoked from a `requestAnimationFrame` after the
      // surrounding action's promise has already resolved, so rethrowing here would only surface as an
      // unhandled error with no way for the caller to react. Log and swallow.
      logging.warn('restoreScrollAnchor failed', {extra: {error: String(err)}});
    }
  };

  // Schedule twice to cover both the immediate React commit after refresh and any follow-up commits
  // (e.g. STRUCTURED_TABLE_INVOKE_MAPPINGS, ENTRY_SERVER_SIDE_UPDATE) that may shift layout further.
  requestAnimationFrame(() => {
    apply();
    requestAnimationFrame(apply);
  });
}
